import { useCallback } from "react";

import {
    useInputValues,
    useAllowancesOutput,
    useUsageOutput,
    useAiCreditsExceeded
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { useFireAnalyticsEvents } from "@/hooks/use-fire-analytics-events";

import { mapCollectionsValueToAnalyticsPayload } from "./util.ts";

export const useGrowthSliderTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireSliderChangedEvent = useCallback(
        (multiplierValue: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "slider",
                actionSubjectId: "ubpGrowthSlider",
                attributes: {
                    multiplierValue
                }
            });
        },
        [fireUIEvent]
    );

    return { fireSliderChangedEvent };
};

export const useAiChatAgentsTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireChatAdoptionChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "textField",
                actionSubjectId: "ubpChatAdoptionPercent",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    const fireChatQueriesPerUserChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "textField",
                actionSubjectId: "ubpChatUsersPerRulePerMonth",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return { fireChatAdoptionChangedEvent, fireChatQueriesPerUserChangedEvent };
};

export const useAiAutomationTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireAutomationActiveRulesChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "textField",
                actionSubjectId: "ubpAutomationActiveRules",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    const fireAutomationRunsPerRuleChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "textField",
                actionSubjectId: "ubpAutomationRunsPerRulePerMonth",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return {
        fireAutomationActiveRulesChangedEvent,
        fireAutomationRunsPerRuleChangedEvent
    };
};

export const useSolutionPlansTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const firePlanChangedEvent = useCallback(
        (collectionKey: string, plan: string) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "ubpSolutionPlan",
                attributes: {
                    collectionKey,
                    plan
                }
            });
        },
        [fireUIEvent]
    );

    return { firePlanChangedEvent };
};

export const useIndustrySelectorTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireCollectionToggledEvent = useCallback(
        (collectionKey: string, selected: boolean) => {
            fireUIEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: "ubpIndustryCollectionPill",
                attributes: {
                    collectionKey,
                    selected
                }
            });
        },
        [fireUIEvent]
    );

    return { fireCollectionToggledEvent };
};

export const useFeedbackButtonsTracking = () => {
    const { collections, ...input } = useInputValues();
    const allowances = useAllowancesOutput();
    const usage = useUsageOutput();
    const aiCreditsExceeded = useAiCreditsExceeded();

    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireFeedbackClickedEvent = (vote: "up" | "down") => {
        fireUIEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "ubpFeedbackButton",
            attributes: {
                vote,
                input: {
                    usecases:
                        mapCollectionsValueToAnalyticsPayload(collections),
                    ...input
                },
                allowances,
                usage,
                aiCreditsExceeded
            }
        });
    };

    return { fireFeedbackClickedEvent };
};

export const useTeamSizeTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireTeamSizeChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "textField",
                actionSubjectId: "ubpTeamSize",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return { fireTeamSizeChangedEvent };
};

export const useIndexedObjectsTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireIndexed3rdPartyToolsChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "ubpIndexedObjects3rdPartyTools",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    const fireIndexedObjectsPerToolChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "ubpIndexedObjectsPerTool",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return {
        fireIndexed3rdPartyToolsChangedEvent,
        fireIndexedObjectsPerToolChangedEvent
    };
};
